import { Feature } from '@gig/magic-messages';
import { getFeatureEnabled, configQuery } from '@gig/magic-common';

interface IAlternateLink {
    baseUrl: string;
    hreflang: string;
}

export class AlternateLinkService {
    private baseUrl = '';
    private hreflang = '';

    setAlternateTag(url: string, options: { useBaseUrl: boolean }) {
        this.isEnabled().then((enabled) => {
            if (!enabled) {
                return;
            }

            const tag = this.queryOrCreateTag();
            tag.setAttribute('rel', 'alternate');
            tag.setAttribute('hreflang', this.hreflang);
            tag.setAttribute('data-v-test-id', 'alternate-tag');

            const href = options.useBaseUrl ? this.concatBaseUrl(url) : url;
            tag.setAttribute('href', href);
        });
    }

    destroyAlternateTag() {
        this.isEnabled().then((enabled) => {
            if (!enabled) {
                return;
            }

            const tag = this.queryTag();
            if (tag) {
                tag.remove();
            }
        });
    }

    private concatBaseUrl(url: string) {
        if (url.startsWith('/')) {
            url = url.substring(1);
        }

        if (this.baseUrl.endsWith('/')) {
            return this.baseUrl + url;
        }

        return this.baseUrl + '/' + url;
    }

    private async isEnabled() {
        const enabled = await getFeatureEnabled(Feature.HREFLANG_ON_PAGE);
        if (!enabled) {
            return false;
        }

        await this.queryConfig();
        if (!this.baseUrl || !this.hreflang) {
            return false;
        }

        return true;
    }

    private queryOrCreateTag() {
        const tag = this.queryTag();
        if (tag) {
            return tag;
        }

        const createdTag = document.createElement('link');
        document.head.appendChild(createdTag);
        return createdTag;
    }

    private queryTag() {
        return document.querySelector<HTMLLinkElement>(`link[rel=alternate]`);
    }

    private async queryConfig() {
        const config = await configQuery<{ alternateLink: IAlternateLink }>(
            'seo',
        );
        if (
            !config?.alternateLink?.baseUrl ||
            !config?.alternateLink?.hreflang
        ) {
            // eslint-disable-next-line no-console
            console.log(
                `Alternate link not configured properly. Use the "seo.alternateLink" configuration property to set the base url and hreflang. Example: { baseUrl: 'https://www.mycasino.com/', hreflang: 'en-ca' }`,
            );
            return;
        }

        this.baseUrl = config.alternateLink.baseUrl;
        this.hreflang = config.alternateLink.hreflang;
    }
}

export const alternateLink = new AlternateLinkService();
